import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { getSnapshot } from 'mobx-state-tree'
import {
  EditRounded,
  GroupAddRounded,
  PauseCircleRounded,
  PlayCircleRounded,
} from '@mui/icons-material'
import { GridCellParams } from '@mui/x-data-grid-pro'
import { Link } from 'react-router-dom'

import { useStores } from '@trivie/core'
import { DataTable } from '@trivie/ui-web/components/data-table'
import { initColumns } from '@trivie/ui-web/components/data-table/configure'
import { Toolbar } from '@trivie/ui-web/components/table-toolbar'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'

import { SubscriptionChip } from './subscription-chip'
import { GiftSubscriptionDialog } from './gift-subscription/gift-subscription-dialog'

import { max } from 'lodash'

interface SubscriptionProductsTableProps {
  onEdit?: (product: any) => void
}

export const SubscriptionProductsTable = observer(({ onEdit }: SubscriptionProductsTableProps) => {
  const { subscriptionStore, manageUserStore } = useStores()
  const { permissions } = manageUserStore
  const confirm = useConfirm()

  const [selectedRows, setSelectedRows] = useState<any>([])
  const [tableActions, setTableActions] = useState<any>([])
  const [showGiftDialog, setShowGiftDialog] = useState(false)

  useEffect(() => {
    subscriptionStore.getProducts()
  }, [])

  const { columns } = initColumns([
    {
      field: 'title',
      headerName: 'Title',
      description: 'Title of the subscription',
      data_type: 'str',
      width: 200,
      renderCell: (params: GridCellParams) => (
        <Link
          style={{
            display: 'block',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
          to={`/activities/subscriptions/${params.id}/overview`}
        >
          {params.formattedValue as string}
        </Link>
      ),
    },
    {
      field: 'sku',
      description: 'Product SKU value',
      headerName: 'SKU',
      data_type: 'str',
      width: 150,
    },
    {
      field: 'description',
      description: 'A description of the subscription',
      headerName: 'Description',
      data_type: 'str',
      width: 250,
    },
    {
      field: 'duration_days',
      description: 'The duration the subscription is valid for (days)',
      headerName: 'Duration',
      width: 120,
      data_type: 'number',
      valueFormatter: (value: number | null) => (value != null ? `${value} days` : 'Open'),
    },
    {
      field: 'is_active',
      description: 'Whether or not the subscription is currently active',
      headerName: 'Status',
      possible_values: ['Active', 'Inactive'],
      data_type: 'str',
      operators: ['eq', 'ne'],
      width: 120,
      valueGetter: (value: boolean) => (value ? 'Active' : 'Inactive'),
      renderCell: (params: GridCellParams) => (
        <SubscriptionChip status={params.value === 'Active' ? 'active' : 'inactive'} />
      ),
    },
  ])

  const getStatusToggleAction = () => {
    const product = subscriptionStore.products.find((p) => p.id === selectedRows[0])
    const action = product?.is_active ? 'deactivate' : 'activate'
    const label = product?.is_active ? 'Deactivate' : 'Activate'
    const icon = product?.is_active ? PauseCircleRounded : PlayCircleRounded
    return { action, label, icon, product }
  }

  const getActions = () => {
    const { label: toggleLabel, icon: toggleIcon } = getStatusToggleAction()

    const hasInactive = selectedRows.some(
      (id: number) => !subscriptionStore.products.find((p) => p.id === id)?.is_active,
    )

    return [
      {
        label: 'Add Subscribers',
        icon: GroupAddRounded,
        onClick: () => setShowGiftDialog(true),
        disabled:
          hasInactive || !permissions?.permissions.some((p) => p.code === 'can_edit_product'),
      },
      {
        label: 'Edit Subscription',
        icon: EditRounded,
        disabled:
          selectedRows.length !== 1 ||
          !permissions?.permissions.some((p) => p.code === 'can_edit_product'),

        onClick: () => {
          const product = subscriptionStore.products.find((p) => p.id === selectedRows[0])
          if (product && onEdit) onEdit(product)
        },
      },
      {
        label: toggleLabel,
        icon: toggleIcon,
        color: !hasInactive ? 'danger' : '',
        disabled:
          selectedRows.length !== 1 ||
          !permissions?.permissions.some((p) => p.code === 'can_edit_product'),
        onClick: () => {
          const { action, product } = getStatusToggleAction()
          if (!product) return

          let success: any
          confirm({
            danger: action === 'deactivate',
            title: `${action === 'deactivate' ? 'Deactivate' : 'Activate'} Product`,
            dialogText: `Are you sure you want to ${action} "${product.title}"?`,
          })
            .then(async () => {
              if (action === 'deactivate') {
                success = await subscriptionStore.archiveProducts(selectedRows)
              } else {
                success = await subscriptionStore.updateProduct(product.id, {
                  ...product,
                  content: (product.content || []).map((c) => ({
                    ...c,
                    id: String(c.id),
                  })),
                  is_active: !product.is_active,
                })
              }
              setSelectedRows([])
              if (!success) {
                confirm({
                  danger: true,
                  title: 'Error',
                  dialogText: `There was an issue ${action === 'deactivate' ? 'deactivating' : 'activating'} "${product.title}"`,
                  hideCancelButton: true,
                })
              } else {
                confirm({
                  title: `${action === 'deactivate' ? 'Deactivate' : 'Activate'} Product`,
                  dialogText: `"${product.title}" was successfully ${action === 'deactivate' ? 'deactivated' : 'activated'}`,
                  hideCancelButton: true,
                })
                subscriptionStore.getProducts()
              }
            })
            .catch(() => {})
        },
      },
    ]
  }

  useEffect(() => {
    setTableActions(getActions())
  }, [selectedRows])

  return (
    <>
      <DataTable
        toolbar={<Toolbar selected={selectedRows} actions={tableActions} />}
        searchPlaceholder="Search subscriptions"
        columns={columns as any}
        rows={subscriptionStore.products.map((p) => ({ ...getSnapshot(p) }))}
        getRowId={(row) => row.id}
        onRowSelectionModelChange={(param) => setSelectedRows(param)}
        rowSelectionModel={selectedRows}
        onSaveFilters={() => {}}
      />
      <GiftSubscriptionDialog
        open={showGiftDialog}
        requestClose={() => {
          setShowGiftDialog(false)
          setSelectedRows([])
        }}
        defaultDuration={max(
          selectedRows.map(
            (id) => subscriptionStore.products.find((p) => p.id === id)?.duration_days,
          ),
        )}
        productIds={selectedRows}
      />
    </>
  )
})
